import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, MapPin, Tag } from "lucide-react";
import Seo from "../components/Seo";
import { searchRetreats } from "../services/api";
import { getOfferingListingPath } from "../lib/offeringRoutes";
import { formatProviderName } from "../lib/groupTeacherListings";
import { ONLINE_COURSE, PRENATAL_YOGA } from "../lib/courseTypes";
import { resolveBasePriceInr } from "../lib/retreatSlug";
import { useCurrency } from "../context/CurrencyContext";
import { formatFromInr } from "../lib/currency";

function ProviderCoursesPage({ listingType = ONLINE_COURSE }) {
  const { source } = useParams();
  const providerSource = decodeURIComponent(source || "").trim().toLowerCase();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { currency, rates } = useCurrency();

  const listingPath = getOfferingListingPath(listingType);
  const listingLabel = listingType === PRENATAL_YOGA ? "Prenatal Yoga" : "Online Courses";
  const providerName = formatProviderName(providerSource);

  useEffect(() => {
    if (!providerSource) return;
    setLoading(true);
    setError("");
    searchRetreats({
      type: listingType,
      source: providerSource,
      limit: 50,
      offset: 0,
      sort: "latest",
    })
      .then(({ items: rows }) => {
        const schoolRows = (rows || []).filter(
          (row) =>
            String(row.source || "").toLowerCase() === providerSource &&
            row.item_source !== "teacher_offering" &&
            row.source !== "yoga_teachers"
        );
        setItems(schoolRows);
      })
      .catch(() => setError("Could not load courses for this school."))
      .finally(() => setLoading(false));
  }, [providerSource, listingType]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
      </div>
    );
  }

  if (error || !providerSource || items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <p className="text-gray-600 mb-4">{error || "No courses found for this school."}</p>
          <Link
            to={listingPath}
            className="text-emerald-600 hover:text-emerald-700 font-semibold inline-flex items-center gap-1"
          >
            <ArrowLeft size={16} /> Back to {listingLabel}
          </Link>
        </div>
      </div>
    );
  }

  const location = items[0]?.location || "India";
  const thumb = items[0]?.thumbnail_url
    ? String(items[0].thumbnail_url).startsWith("http") ||
      String(items[0].thumbnail_url).startsWith("data:")
      ? items[0].thumbnail_url
      : `/${String(items[0].thumbnail_url).replace(/^\/+/, "")}`
    : "/images/nalanda.jpg";

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <Seo
        title={`${providerName} Online Courses`}
        description={`Browse ${items.length} online yoga courses from ${providerName}.`}
        canonicalPath={`${listingPath}/provider/${encodeURIComponent(providerSource)}`}
      />

      <div className="relative overflow-hidden bg-gradient-to-br from-emerald-900 via-teal-900 to-gray-900 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.12),transparent_45%)]" />
        <div className="relative max-w-5xl mx-auto px-4 py-10 md:py-14">
          <Link
            to={listingPath}
            className="inline-flex items-center gap-1 text-sm text-emerald-100 hover:text-white mb-8"
          >
            <ArrowLeft size={14} /> Back to {listingLabel}
          </Link>
          <div className="flex flex-col md:flex-row items-center md:items-end gap-8">
            <div className="w-36 h-36 md:w-44 md:h-44 rounded-3xl overflow-hidden bg-white/10 ring-4 ring-white/20 shadow-2xl shrink-0">
              <img src={thumb} alt={providerName} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 text-center md:text-left pb-2">
              <p className="text-emerald-200 text-xs font-bold uppercase tracking-[0.25em] mb-3">
                Yoga School
              </p>
              <h1 className="text-3xl md:text-5xl font-black tracking-tight leading-tight mb-3">
                {providerName}
              </h1>
              <p className="text-emerald-50/90 text-base md:text-lg mb-3">
                {items.length} online {items.length === 1 ? "course" : "courses"}
              </p>
              <p className="flex items-center justify-center md:justify-start gap-2 text-sm text-emerald-100/90">
                <MapPin size={15} className="text-emerald-300" />
                {location}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-10 space-y-4">
        {items.map((course) => {
          const path = course.slug ? `/retreats/${course.slug}` : null;
          const priceLabel = (() => {
            const baseInr = resolveBasePriceInr(course, rates);
            return baseInr != null ? formatFromInr(baseInr, currency, rates) : null;
          })();
          return (
            <div
              key={course.retreat_key || course.slug}
              className="bg-white rounded-2xl border border-gray-100 p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between"
            >
              <div>
                <h2 className="text-lg font-bold text-gray-900">{course.retreat_name}</h2>
                <div className="mt-2 flex flex-wrap gap-3 text-sm text-gray-600">
                  {course.duration && (
                    <span className="inline-flex items-center gap-1">
                      <Clock size={14} className="text-blue-500" /> {course.duration}
                    </span>
                  )}
                  {course.location && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin size={14} className="text-emerald-500" /> {course.location}
                    </span>
                  )}
                  {course.type && (
                    <span className="inline-flex items-center gap-1">
                      <Tag size={14} /> {course.type}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-sm font-semibold text-gray-800">
                  {priceLabel ? `from ${priceLabel}` : "Price on request"}
                </span>
                {path ? (
                  <Link
                    to={path}
                    className="rounded-lg bg-emerald-600 text-white text-sm font-semibold px-4 py-2 hover:bg-emerald-700"
                  >
                    View course
                  </Link>
                ) : course.source_url ? (
                  <a
                    href={course.source_url}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-lg bg-emerald-600 text-white text-sm font-semibold px-4 py-2 hover:bg-emerald-700"
                  >
                    View course
                  </a>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ProviderCoursesPage;
